import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCog, FaCogs } from 'react-icons/fa';
import styled from 'styled-components';
import { UiConstants } from '../../data/UiConstants';
import { IntInputField } from '../shared/IntInputField';
import { MenuCard, MenuCardOverflowUnset } from '../menu/MenuCard';
import { MenuCardLabel } from '../menu/MenuCardLabel';
import { Overlay } from '../menu/PopupSubMenu';
import { LanguageSwitcher } from './LanguageSwitcher';
import { KeyValueRow, ListEntryKey, ListEntryValue } from "../shared/KeyValueRow";
import { Input } from "./Input";
import { emptyFunctionToSuppressReactWarning } from '../../helper/reactHelpers';
import { Dropdown } from './Dropdown';
import { allEditorComplexities, localSettingsStore } from '../../stores/LocalSettingsStore';
import { MapSelector } from '../mapEditor/MapSelector';
import { FloatInputField } from '../shared/FloatInputField';
import { anyFeatureSwitchParameterSet } from '../../data/featureSwitchConstants';
import { LogoutButton } from '../auth/LogoutButton';
import { gameStore } from '../../stores/GameStore';
import { networkDiagnosticsStore } from '../../stores/NetworkDiagnosticsStore';
import { userStore } from '../../stores/UserStore';
import { actionEditorStore } from '../../stores/ActionEditorStore';
import { editorStore } from '../../stores/EditorStore';
const MenuContainer = styled.div.withConfig({
  displayName: "SettingsMenu__MenuContainer",
  componentId: "sc-1uju6b8-0"
})(["z-index:", ";border-radius:", ";position:fixed;right:50%;transform:translate(50%,0);top:70px;background:", ";"], UiConstants.Z_INDEX_MODAL, UiConstants.BORDER_RADIUS, UiConstants.COLOR_MENU_BACKGROUND);
const MenuContent = styled.div.withConfig({
  displayName: "SettingsMenu__MenuContent",
  componentId: "sc-1uju6b8-1"
})(["max-height:450px;overflow-y:auto;"]);
const ShortListEntryKey = styled(ListEntryKey).withConfig({
  displayName: "SettingsMenu__ShortListEntryKey",
  componentId: "sc-1uju6b8-2"
})(["min-width:150px;"]);
export const IconButton = styled.span.withConfig({
  displayName: "SettingsMenu__IconButton",
  componentId: "sc-1uju6b8-3"
})(["margin-left:2px;padding-top:2px;padding-bottom:2px;padding-left:6px;padding-right:6px;border-radius:", ";background-color:", ";color:white;cursor:pointer;&:hover{cursor:pointer;background-color:", ";}svg{position:relative;top:2px;}"], UiConstants.BORDER_RADIUS, UiConstants.COLOR_DARK_BUTTON, UiConstants.COLOR_DARK_BUTTON_HOVER);
const SettingsMenuTab = styled.span.withConfig({
  displayName: "SettingsMenu__SettingsMenuTab",
  componentId: "sc-1uju6b8-4"
})(["margin-left:2px;padding-top:2px;padding-bottom:2px;padding-left:6px;padding-right:6px;border-radius:", ";background-color:", ";border:", ";color:white;width:min-content;&:hover{cursor:pointer;background-color:", ";}text-decoration:none;"], UiConstants.BORDER_RADIUS, props => props.active ? UiConstants.COLOR_SELECTION_HIGHLIGHT : UiConstants.COLOR_DARK_BUTTON, props => props.active ? "2px solid black" : "unset", props => props.active ? UiConstants.COLOR_SELECTION_HIGHLIGHT : UiConstants.COLOR_DARK_BUTTON_HOVER);

const NumberInput = ({
  label,
  value,
  valueSetter
}) => {
  return /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, label), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(IntInputField, {
    value: value,
    onChange: valueSetter,
    onlyPositive: true
  })));
};

const FloatInput = ({
  label,
  value,
  valueSetter
}) => {
  return /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, label), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(FloatInputField, {
    value: value,
    onChange: valueSetter,
    onlyPositive: true
  })));
};

const ClientSettings = observer(({
  showNonGameSettings
}) => {
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/React.createElement(MenuCard, null, /*#__PURE__*/React.createElement(KeyValueRow, null, /*#__PURE__*/React.createElement(ShortListEntryKey, null, t("editor.language")), /*#__PURE__*/React.createElement(ListEntryValue, null, /*#__PURE__*/React.createElement(LanguageSwitcher, null))), /*#__PURE__*/React.createElement(KeyValueRow, {
    onClick: () => gameStore.toggleAccessibilityOptions()
  }, /*#__PURE__*/React.createElement(ShortListEntryKey, null, t("game.accessibility_options")), /*#__PURE__*/React.createElement(ListEntryValue, null, /*#__PURE__*/React.createElement(Input, {
    type: "checkbox",
    checked: gameStore.accessibilityOptions,
    onChange: emptyFunctionToSuppressReactWarning
  }))), /*#__PURE__*/React.createElement(KeyValueRow, {
    onClick: () => localSettingsStore.toggleFullscreen()
  }, /*#__PURE__*/React.createElement(ShortListEntryKey, null, t("editor.local_setting_toggle_fullscreen")), /*#__PURE__*/React.createElement(ListEntryValue, null, /*#__PURE__*/React.createElement(Input, {
    type: "checkbox",
    checked: localSettingsStore.fullscreen,
    onChange: emptyFunctionToSuppressReactWarning
  }))), userStore.shouldShowAdvancedOptions && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(KeyValueRow, {
    onClick: () => localSettingsStore.toggleShowDebugInfoSetting()
  }, /*#__PURE__*/React.createElement(ShortListEntryKey, null, t("editor.local_setting_show_debug_info")), /*#__PURE__*/React.createElement(ListEntryValue, null, /*#__PURE__*/React.createElement(Input, {
    type: "checkbox",
    checked: localSettingsStore.showDebugInfo,
    onChange: emptyFunctionToSuppressReactWarning
  }))), /*#__PURE__*/React.createElement(KeyValueRow, {
    onClick: () => localSettingsStore.toggleShowPerformanceInfoSetting()
  }, /*#__PURE__*/React.createElement(ShortListEntryKey, null, t("editor.local_setting_show_performance_info")), /*#__PURE__*/React.createElement(ListEntryValue, null, /*#__PURE__*/React.createElement(Input, {
    type: "checkbox",
    checked: localSettingsStore.showPerformanceInfo,
    onChange: emptyFunctionToSuppressReactWarning
  })))), showNonGameSettings && /*#__PURE__*/React.createElement(React.Fragment, null, !actionEditorStore.treeFocussedForWorkshop && /*#__PURE__*/React.createElement(KeyValueRow, null, /*#__PURE__*/React.createElement(ShortListEntryKey, null, t("editor.local_setting_complexity")), /*#__PURE__*/React.createElement(ListEntryValue, null, /*#__PURE__*/React.createElement(Dropdown, {
    value: localSettingsStore.editorComplexity,
    onChange: e => localSettingsStore.setEditorComplexity(+e.target.value)
  }, allEditorComplexities.map(complexity => /*#__PURE__*/React.createElement("option", {
    key: complexity,
    value: complexity
  }, t("editor.local_setting_complexity_" + complexity)))))), userStore.shouldShowAdvancedOptions && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(KeyValueRow, {
    onClick: localSettingsStore.toggleActionTreeValidationEnabled
  }, /*#__PURE__*/React.createElement(ShortListEntryKey, null, t("editor.action_tree_validation")), /*#__PURE__*/React.createElement(ListEntryValue, null, /*#__PURE__*/React.createElement(Input, {
    type: "checkbox",
    checked: localSettingsStore.actionTreeValidationEnabled,
    onChange: emptyFunctionToSuppressReactWarning
  }))), /*#__PURE__*/React.createElement(KeyValueRow, {
    onClick: localSettingsStore.toggleShowUndoHistoryDebug
  }, /*#__PURE__*/React.createElement(ShortListEntryKey, null, t("editor.local_setting_show_undo_history_debug")), /*#__PURE__*/React.createElement(ListEntryValue, null, /*#__PURE__*/React.createElement(Input, {
    type: "checkbox",
    checked: localSettingsStore.showUndoHistoryDebug,
    onChange: emptyFunctionToSuppressReactWarning
  }))))), userStore.isLoggedIn && /*#__PURE__*/React.createElement(KeyValueRow, null, /*#__PURE__*/React.createElement(LogoutButton, null)), /*#__PURE__*/React.createElement(KeyValueRow, null, /*#__PURE__*/React.createElement("button", {
    onClick: () => networkDiagnosticsStore.setShowBecauseOfUserRequest(true)
  }, t("editor.network_diagnostics"))));
});
const ServerSettings = observer(() => {
  const {
    t
  } = useTranslation();

  const newReputationActBalance = (act, value) => {
    const newActBalance = gameStore.gameDesignVariables.reputationActBalance.slice();
    newActBalance[act - 1] = value;
    gameStore.gameDesignVariables.setReputationActBalance(newActBalance);
  };

  return /*#__PURE__*/React.createElement(MenuCard, null, /*#__PURE__*/React.createElement(MenuCardLabel, null, t("editor.game_design_variable_general")), /*#__PURE__*/React.createElement("table", null, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.game_design_variable_game_starting_map")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(MapSelector, {
    selectedMapId: gameStore.gameDesignVariables.gameStartingMapId,
    onSelected: mapId => gameStore.gameDesignVariables.setGameStartingMapId(mapId)
  }))))), /*#__PURE__*/React.createElement(MenuCardLabel, null, t("action_editor.property_reputation")), /*#__PURE__*/React.createElement("table", null, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement(NumberInput, {
    label: t("editor.game_design_variable_reputation_balance_factor"),
    value: gameStore.gameDesignVariables.reputationBalanceFactor,
    valueSetter: value => gameStore.gameDesignVariables.setReputationBalanceFactor(value)
  }), /*#__PURE__*/React.createElement(NumberInput, {
    label: t("editor.game_design_variable_reputation_change_small"),
    value: gameStore.gameDesignVariables.reputationAmountBalance.Small,
    valueSetter: value => gameStore.gameDesignVariables.reputationAmountBalance.setSmall(value)
  }), /*#__PURE__*/React.createElement(NumberInput, {
    label: t("editor.game_design_variable_reputation_change_reasonable"),
    value: gameStore.gameDesignVariables.reputationAmountBalance.Reasonable,
    valueSetter: value => gameStore.gameDesignVariables.reputationAmountBalance.setReasonable(value)
  }), /*#__PURE__*/React.createElement(NumberInput, {
    label: t("editor.game_design_variable_reputation_change_large"),
    value: gameStore.gameDesignVariables.reputationAmountBalance.Large,
    valueSetter: value => gameStore.gameDesignVariables.reputationAmountBalance.setLarge(value)
  }), /*#__PURE__*/React.createElement(FloatInput, {
    label: t("editor.game_design_variable_reputation_balance_in_act", {
      number: 1
    }),
    value: gameStore.gameDesignVariables.reputationActBalance[0],
    valueSetter: value => newReputationActBalance(1, value)
  }), /*#__PURE__*/React.createElement(FloatInput, {
    label: t("editor.game_design_variable_reputation_balance_in_act", {
      number: 2
    }),
    value: gameStore.gameDesignVariables.reputationActBalance[1],
    valueSetter: value => newReputationActBalance(2, value)
  }), /*#__PURE__*/React.createElement(FloatInput, {
    label: t("editor.game_design_variable_reputation_balance_in_act", {
      number: 3
    }),
    value: gameStore.gameDesignVariables.reputationActBalance[2],
    valueSetter: value => newReputationActBalance(3, value)
  }), /*#__PURE__*/React.createElement(FloatInput, {
    label: t("editor.game_design_variable_reputation_balance_in_act", {
      number: 4
    }),
    value: gameStore.gameDesignVariables.reputationActBalance[3],
    valueSetter: value => newReputationActBalance(4, value)
  }), /*#__PURE__*/React.createElement(FloatInput, {
    label: t("editor.game_design_variable_reputation_balance_in_act", {
      number: 5
    }),
    value: gameStore.gameDesignVariables.reputationActBalance[4],
    valueSetter: value => newReputationActBalance(5, value)
  }))));
});
export const SettingsMenu = ({
  showNonGameSettings
}) => {
  const {
    t
  } = useTranslation();
  const [menuTab, setMenuTab] = useState("client");
  const [menuOpen, setMenuOpen] = useState(false);
  const showServerSettings = showNonGameSettings && editorStore.isMainGameEditor;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IconButton, {
    onClick: () => setMenuOpen(true)
  }, anyFeatureSwitchParameterSet ? /*#__PURE__*/React.createElement(FaCogs, null) : /*#__PURE__*/React.createElement(FaCog, null)), menuOpen && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Overlay, {
    onClick: () => setMenuOpen(false)
  }), /*#__PURE__*/React.createElement(MenuContainer, null, showServerSettings && /*#__PURE__*/React.createElement(MenuCardOverflowUnset, null, /*#__PURE__*/React.createElement(SettingsMenuTab, {
    active: menuTab === "client",
    onClick: () => setMenuTab("client")
  }, t("editor.client_settings")), /*#__PURE__*/React.createElement(SettingsMenuTab, {
    active: menuTab === "server",
    onClick: () => setMenuTab("server")
  }, t("editor.server_settings"))), /*#__PURE__*/React.createElement(MenuContent, null, (menuTab === "client" || !showServerSettings) && /*#__PURE__*/React.createElement(ClientSettings, {
    showNonGameSettings: showNonGameSettings
  }), menuTab === "server" && showServerSettings && /*#__PURE__*/React.createElement(ServerSettings, null)))));
};